import { FOCUS_RING, Icon, PositionPair } from '@amelio/ui-library';
import { useMemo } from 'react';

import {
    GROWTH_GLASS_SURFACE,
    GROWTH_PANEL_SURFACE,
    GROWTH_SECTION_PADDING,
    GROWTH_SECTION_TITLE,
} from './growthStyles';
import { readPositionName } from './targetPosition';

import type { ProficiencyLevelResponse } from '@/api/performance-profile/performanceProfileService';
import type { CareerStep, NextLevelSkill, TargetPosition } from '@/types/career-growth';
import type { OptionProps } from '@amelio/ui-library';

import { EvolutionBar } from '@/components/CareerGrowth/EvolutionBar';
import { computeSkillReadiness } from '@/components/CareerGrowth/skillReadiness';
import { SkillsGrid } from '@/components/CareerGrowth/SkillsGrid';
import { useAmelioTranslations } from '@/hooks';
import { getLocalizedText } from '@/utils/localization';

interface NextTargetCardProps {
    currentStep: CareerStep | null;
    currentProfileName: string | null;
    /**
     * Where the employee is heading, as the API reconciles it: a role and the competency profile
     * chosen with it, from whichever of the target step and the latest aspiration is the more
     * recent. `null` when neither names one.
     */
    targetPosition: TargetPosition | null;
    nextLevelSkills: NextLevelSkill[];
    /** The company's proficiency scale; `undefined` while it loads or when it failed to. */
    proficiencyLevels: ProficiencyLevelResponse[] | undefined;
    areLevelsInError: boolean;
    employeeFirstName: string;
    /** Opens the career steps editor on the current step. */
    onEditCurrentStep: () => void;
    /** Opens the page's competency-profile assignment flow. */
    onEditCurrentProfile: () => void;
    /** Opens the screen that names the target position and assigns its competency profile. */
    onEditTargetProfile: () => void;
    dataTestId?: string;
}

/**
 * "Prochaine cible": where the employee stands, where they aim, how far along they are and what
 * the target profile still asks of them.
 *
 * Two empty states, because two things can be missing. With neither a current profile nor a target
 * at all, the card offers both "pick a profile" calls to action instead of an empty progression.
 * With a target that carries no competency profile, it offers that one call to action inside the
 * evolution journey card, where the journey it would unlock is drawn, in place of a sentence about
 * competencies that cannot exist yet.
 */
export const NextTargetCard = ({
    currentStep,
    currentProfileName,
    targetPosition,
    nextLevelSkills,
    proficiencyLevels,
    areLevelsInError,
    employeeFirstName,
    onEditCurrentStep,
    onEditCurrentProfile,
    onEditTargetProfile,
    dataTestId = 'growth-next-target',
}: NextTargetCardProps) => {
    const { t } = useAmelioTranslations();

    // Without the scale, every competency would read as never assessed: nothing is concluded
    // about the employee until it is known.
    const readiness = useMemo(
        () =>
            proficiencyLevels ? computeSkillReadiness(nextLevelSkills, proficiencyLevels) : null,
        [nextLevelSkills, proficiencyLevels],
    );

    const targetTitle = targetPosition && readPositionName(targetPosition);
    // A profile named in no language is as good as none: the tile then says "undefined" instead of
    // leaving its label without a value.
    const originProfileName =
        getLocalizedText(targetPosition?.competencyProfileName ?? undefined) || null;
    const targetProfileName =
        originProfileName && targetPosition?.isAdjusted
            ? t('performanceProfile.growth.nextTarget.adjustedProfile', { name: originProfileName })
            : originProfileName;
    const hasTargetProfile = !!targetPosition?.competencyProfileId;

    const hasNothingToTrack = currentProfileName === null && !targetPosition;
    const profileLabel = t('performanceProfile.growth.nextTarget.competencyProfile');
    const undefinedPlaceholder = t('performanceProfile.growth.nextTarget.undefined');

    const currentActions: OptionProps[] = [
        {
            id: 'edit-current-step',
            label: t('performanceProfile.growth.nextTarget.editCurrentStep'),
            onClick: onEditCurrentStep,
        },
        {
            id: 'edit-current-profile',
            label: t('performanceProfile.growth.nextTarget.editCurrentProfile'),
            onClick: onEditCurrentProfile,
        },
    ];

    // Only says why the grid is absent: with competencies to show, the grid shows them all, and
    // "every level is reached" becomes its lead rather than a sentence standing in for them.
    const readSkillsMessage = () => {
        if (!readiness) {
            return areLevelsInError
                ? t('performanceProfile.growth.score.levelsError')
                : t('performanceProfile.loading');
        }
        return t('performanceProfile.growth.skills.empty');
    };

    const targetActions: OptionProps[] = [
        {
            id: 'edit-target-step',
            label: t('performanceProfile.growth.nextTarget.editDesiredStep'),
            onClick: onEditTargetProfile,
        },
    ];

    return (
        <section
            className={`${GROWTH_GLASS_SURFACE} ${GROWTH_SECTION_PADDING} flex flex-col gap-[24px] md:gap-[36px]`}
            data-testid={dataTestId}
        >
            <h3 className={GROWTH_SECTION_TITLE}>
                {hasNothingToTrack
                    ? t('performanceProfile.growth.nextTarget.newTarget')
                    : t('performanceProfile.growth.nextTarget.title')}
            </h3>

            {hasNothingToTrack ? (
                <EmptyProfileTargets
                    onPickCurrent={onEditCurrentProfile}
                    onPickTarget={onEditTargetProfile}
                    dataTestId={dataTestId}
                />
            ) : (
                <>
                    <PositionPair
                        currentPosition={{
                            label: t('performanceProfile.growth.nextTarget.currentPosition'),
                            title: currentStep?.title ?? null,
                            profileLabel,
                            profileName: currentProfileName,
                            undefinedPlaceholder,
                            actions: currentActions,
                            menuAriaLabel: t(
                                'performanceProfile.growth.nextTarget.editCurrentAria',
                            ),
                        }}
                        desiredPosition={{
                            label: t('performanceProfile.growth.nextTarget.desiredPosition'),
                            title: targetTitle,
                            profileLabel,
                            profileName: targetProfileName,
                            undefinedPlaceholder,
                            actions: targetActions,
                            menuAriaLabel: t(
                                'performanceProfile.growth.nextTarget.editDesiredAria',
                            ),
                        }}
                        // The prototype's composition: each tile hugs its content, and the desired
                        // one lies flat on the tinted glass while the current one stands out.
                        tileWidth={'content'}
                        desiredSurface={'flat'}
                        dataTestId={`${dataTestId}-positions`}
                    />

                    <EvolutionBar
                        score={readiness?.score ?? null}
                        employeeFirstName={targetTitle ? employeeFirstName : null}
                        dataTestId={`${dataTestId}-evolution`}
                    >
                        {!hasTargetProfile && (
                            <PickProfileButton
                                label={t(
                                    'performanceProfile.growth.nextTarget.emptyProfile.selectDesired',
                                )}
                                accentClasses={
                                    'border-growth-violet-deep/40 hover:border-growth-violet-deep'
                                }
                                badgeClasses={'bg-growth-violet-deep/10 text-growth-violet-deep'}
                                onClick={onEditTargetProfile}
                                dataTestId={`${dataTestId}-pick-target-profile`}
                            />
                        )}
                    </EvolutionBar>

                    {hasTargetProfile &&
                        (readiness && readiness.items.length > 0 ? (
                            <SkillsGrid
                                items={readiness.items}
                                lead={
                                    readiness.gaps.length === 0
                                        ? t('performanceProfile.growth.skills.allReached')
                                        : undefined
                                }
                                dataTestId={`${dataTestId}-skills`}
                            />
                        ) : (
                            <p
                                className={`${GROWTH_PANEL_SURFACE} text-growth-slate px-6 py-6 text-[14px]`}
                                data-testid={`${dataTestId}-skills-empty`}
                                aria-busy={!readiness && !areLevelsInError}
                            >
                                {readSkillsMessage()}
                            </p>
                        ))}
                </>
            )}
        </section>
    );
};

const EmptyProfileTargets = ({
    onPickCurrent,
    onPickTarget,
    dataTestId,
}: {
    onPickCurrent: () => void;
    onPickTarget: () => void;
    dataTestId: string;
}) => {
    const { t } = useAmelioTranslations();

    return (
        <div
            className={'flex flex-col gap-5'}
            data-testid={`${dataTestId}-empty`}
        >
            <p className={'text-navy/80 text-[14px] leading-relaxed'}>
                {t('performanceProfile.growth.nextTarget.emptyProfile.description')}
            </p>
            <div className={'flex flex-col gap-4 sm:flex-row'}>
                <PickProfileButton
                    label={t('performanceProfile.growth.nextTarget.emptyProfile.selectCurrent')}
                    accentClasses={'border-primary/50 hover:border-primary'}
                    badgeClasses={'bg-primary/10 text-primary'}
                    onClick={onPickCurrent}
                    dataTestId={`${dataTestId}-empty-current`}
                />
                <PickProfileButton
                    label={t('performanceProfile.growth.nextTarget.emptyProfile.selectDesired')}
                    accentClasses={'border-growth-violet-deep/40 hover:border-growth-violet-deep'}
                    badgeClasses={'bg-growth-violet-deep/10 text-growth-violet-deep'}
                    onClick={onPickTarget}
                    dataTestId={`${dataTestId}-empty-target`}
                />
            </div>
        </div>
    );
};

const PickProfileButton = ({
    label,
    accentClasses,
    badgeClasses,
    onClick,
    dataTestId,
}: {
    label: string;
    accentClasses: string;
    badgeClasses: string;
    onClick: () => void;
    dataTestId: string;
}) => {
    const { t } = useAmelioTranslations();

    return (
        <button
            type={'button'}
            onClick={onClick}
            className={`flex min-h-24 w-full flex-1 items-center gap-3 rounded-xl border-2 border-dashed bg-white/60 px-5 py-4 text-left transition-colors hover:bg-white ${accentClasses} ${FOCUS_RING}`}
            data-testid={dataTestId}
        >
            <span
                className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full ${badgeClasses}`}
            >
                <Icon
                    name={'essential-add'}
                    size={'sm'}
                    color={'inherit'}
                    ariaHidden
                />
            </span>
            <span className={'flex flex-col'}>
                <span className={'text-navy text-[14px] font-medium'}>{label}</span>
                <span className={'text-growth-slate text-xs'}>
                    {t('performanceProfile.growth.nextTarget.emptyProfile.undefined')}
                </span>
            </span>
        </button>
    );
};
